import { Alert, DialogStep } from "@inductiveautomation/ws-components";
import { Stack } from "@mui/material";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js/pure";
import { useTranslation } from "react-i18next";

import { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { usePostCalculateTaxMutation } from "../../api/training.api";
import {
  BillingAddressForm,
  BillingAddressIdForm,
  CreditCardTokenForm,
  PurchaseTestStepProps,
} from "../../Training.types";
import BillingAddressConfirmationDialog from "./billing_address_confirmation_dialog";
import CreditCardPaymentForm from "./credit_card_payment_form";
import { updateBillingAddressFormValues } from "./utils";

// Stripe recommends loading js.stripe.com on every page so fraud prevention can gather 
// pre-checkout fraud signals. We intentionally defer to the credit-card step only because 
// this site is primarily account management with a single page for low-volume purchase 
// (training certs) for authenticated users, so this tradeoff of potentially less accurate 
// fraud signals for users who don't make it to the credit card step is acceptable in exchange 
// for avoiding unnecessary stripe network calls and the Stripe.js payload on every route. The 
// `/pure` import + lazy loadStripe() call below is the pattern Stripe provides for this case.
// See TS-1718 and https://docs.stripe.com/js/including
let stripePromise: ReturnType<typeof loadStripe> | undefined;
const getStripePromise = () => {
  if (!window._acctConfig?.STRIPE_PUBLIC_KEY) return undefined;
  stripePromise ??= loadStripe(window._acctConfig.STRIPE_PUBLIC_KEY);
  return stripePromise;
};

export const useCreditCardStep = ({
  isOpen,
  onClose,
  onNext,
  purchaseBarProps,
  creditCardFormMethods,
  billingAddressFormMethods,
  certificationType,
  updatePurchaseBarTaxAmount,
  billingEmail,
  rateId,
}: PurchaseTestStepProps & {
  creditCardFormMethods: UseFormReturn<CreditCardTokenForm>;
  billingAddressFormMethods: UseFormReturn<BillingAddressForm>;
  billingAddressVerifyFormMethods: UseFormReturn<BillingAddressIdForm>;
  updatePurchaseBarTaxAmount: (taxAmount: number | undefined) => void;
  billingEmail: string;
  rateId: number;
}): DialogStep => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [avalaraError, setAvalaraError] = useState<"" | "FAILURE" | "ERROR">(
    ""
  );
  const [postCalculateTax, { isLoading: postCalculateTaxLoading }] =
    usePostCalculateTaxMutation();
  const [unconfirmedTaxAmount, setUnconfirmedTaxAmount] = useState<
    number | undefined
  >();
  // logs error if STRIPE_PUBLIC_KEY is missing
  if (!window._acctConfig?.STRIPE_PUBLIC_KEY) {
    console.error("Stripe public key not defined");
  }

  const translationKeyPrefix =
    certificationType?.toLowerCase() === "gold"
      ? "training.actions.purchase_test.dialog.gold.steps.credit_card"
      : "training.actions.purchase_test.dialog.core.steps.credit_card";
  const { t } = useTranslation("translation", {
    keyPrefix: translationKeyPrefix,
  });

  const [validatedAddressValues, setValidatedAddressValues] =
    useState<BillingAddressForm>();

  const requestSalesTaxAndValidatedAddress = () => {
    if (billingAddressValues.country !== "US") {
      const creditCardValues = creditCardFormMethods.getValues();
      setUnconfirmedTaxAmount(0);
      updatePurchaseBarTaxAmount(0);
      onNext(
        {
          creditCard: creditCardValues,
          billingAddress: billingAddressFormMethods.getValues(),
        },
        true
      );
      return;
    }
    void postCalculateTax({
      calculateCertTaxPayload: {
        billingEmail,
        billingAddress1: billingAddressValues.streetAddressLine1,
        billingAddress2: billingAddressValues.streetAddressLine2 ?? "",
        billingCity: billingAddressValues.city,
        billingState: billingAddressValues.stateOrProvince,
        billingCountry: billingAddressValues.country,
        billingZip: billingAddressValues.zipcodeOrPostalCode,
        total: purchaseBarProps.subTotal ?? 0,
        rateId,
      },
    })
      .then(({ data, error }) => {
        // came back as a 200; check that it's both not undefined and not successful
        if (data?.status && data.status !== "SUCCESS") {
          setAvalaraError(data.status as "FAILURE" | "ERROR" | "");
          return;
        }
        // some other kind of error not captured in status property
        if (error) {
          setAvalaraError("FAILURE");
          return;
        }
        setIsModalOpen(true);
        setAvalaraError("");
        if (data.validatedAddress) {
          setValidatedAddressValues({
            streetAddressLine1: data.validatedAddress.billingAddress1,
            city: data.validatedAddress.billingCity,
            zipcodeOrPostalCode: data.validatedAddress.billingZip,
            country: data.validatedAddress.billingCountry,
            stateOrProvince: data.validatedAddress.billingState,
          });
        }
        if (data.salesTaxTotal !== undefined) {
          setUnconfirmedTaxAmount(data.salesTaxTotal);
        }
      })
      .catch(() => {
        setAvalaraError("FAILURE");
      });
  };

  const confirmValidatedAddress = () => {
    const creditCardValues = creditCardFormMethods.getValues();

    if (validatedAddressValues) {
      updateBillingAddressFormValues(
        billingAddressFormMethods,
        validatedAddressValues
      );
      updatePurchaseBarTaxAmount(unconfirmedTaxAmount);
      onNext(
        {
          creditCard: creditCardValues,
          billingAddress: validatedAddressValues,
        },
        true
      );
    }
  };
  const billingAddressValues = billingAddressFormMethods.getValues();

  const allBillingAddressFieldsFilled =
    !!billingAddressValues.streetAddressLine1 &&
    !!billingAddressValues.city &&
    !!billingAddressValues.country &&
    (!billingAddressValues.countryHasState ||
      !!billingAddressValues.stateOrProvince) &&
    !!billingAddressValues.zipcodeOrPostalCode;

  return {
    label: t("label"),
    substeps: [],
    nextDisabled:
      !creditCardFormMethods.formState.isValid ||
      !allBillingAddressFieldsFilled ||
      postCalculateTaxLoading,
    dialogProps: {
      ...(avalaraError
        ? {
            alert: {
              testId: `${certificationType?.toLowerCase()}-avalara-error`,
              variant: "error",
              description:
                avalaraError === "FAILURE"
                  ? t("dialog.alert.avalara_error")
                  : t("dialog.alert.avalara_address_not_found"),
              id: `${certificationType?.toLowerCase()}-avalara-error`,
              closeButtonEventHandler: () => undefined,
            },
          }
        : {}),
      isOpen,
      testId: "purchase-test-dialog-payment",
      showCloseButton: true,
      onClose: () => {
        setAvalaraError("");
        setUnconfirmedTaxAmount(undefined);
        onClose();
      },
      onNext: requestSalesTaxAndValidatedAddress,
      // if the billing address country is not in the US, we can allow the flow to work as before
      // otherwise this component will control when it's appropriate to move to the next step
      skipCurrentStepIndexUpdate: billingAddressValues.country === "US",
      overline: "",
      headline: t("heading"),
      renderContent: (_, goToNextStep) => {
        const stripePromise = getStripePromise();
        return (
        <>
          {stripePromise ? (
            <Elements stripe={stripePromise}>
              <CreditCardPaymentForm
                creditCardFormMethods={creditCardFormMethods}
                billingAddressFormMethods={billingAddressFormMethods}
                certificationType={certificationType}
              />
              <BillingAddressConfirmationDialog
                translationKeyPrefix={translationKeyPrefix}
                onCancel={() => {
                  setUnconfirmedTaxAmount(undefined);
                  updatePurchaseBarTaxAmount(undefined);
                  setIsModalOpen(false);
                }}
                isOpen={isModalOpen}
                certificationType={certificationType}
                billingAddressValues={billingAddressFormMethods.getValues()}
                validatedAddressValues={validatedAddressValues}
                confirmValidatedAddress={() => {
                  setIsModalOpen(false);
                  confirmValidatedAddress();
                  goToNextStep();
                }}
              />
            </Elements>
          ) : (
            <Stack gap="1rem">
              <Alert
                id="no-stripe-key"
                testId="no-stripe-key"
                description={t("no_stripe_key")}
                variant="error"
                closeButtonEventHandler={() => undefined}
              />
            </Stack>
          )}
        </>
        );
      },
      purchaseBarProps,
    },
  };
};
